import { useEffect, type ReactNode } from 'react';
import {
	Drawer as ChakraDrawer,
	DrawerOverlay,
	DrawerContent,
	DrawerHeader,
	DrawerBody,
	DrawerFooter,
	DrawerCloseButton,
	type DrawerProps as ChakraDrawerProps,
	type HTMLChakraProps,
} from '@chakra-ui/react';
import { useLayout } from '../../hooks/useLayout';

/**
 * `containerProps` marker for side drawers that should coexist with the page.
 * Global CSS in src/index.css scopes pointer-events to this attribute so the
 * left-hand page stays clickable while the drawer is open (without affecting
 * regular centered modals).
 */
export const SIDE_DRAWER_CONTAINER_PROPS = {
	'data-partbay-side-drawer': 'true',
} as unknown as HTMLChakraProps<'div'>;

/**
 * Drawer / SideDrawer primitive — the single side-panel shell. Owns the
 * coexist-with-page behavior (pointer-events passthrough + optional layout
 * shift body class) that was previously wired by hand at each call site.
 *
 * Contract:
 * - `coexistWithPage`: when true, the page behind stays interactive and the
 *   drawer overlay is suppressed.
 * - `layoutShiftBodyClass`: optional body class toggled while open, so the page
 *   wrapper can narrow to sit beside the drawer (see src/index.css).
 * - On the mobile layout, size defaults to `full` so the panel is usable on
 *   a phone-width window. Pass `size` to override.
 */
export interface DrawerProps extends Omit<ChakraDrawerProps, 'children'> {
	title?: ReactNode;
	footer?: ReactNode;
	children: ReactNode;
	showCloseButton?: boolean;
	coexistWithPage?: boolean;
	layoutShiftBodyClass?: string;
}

export const Drawer = ({
	title,
	footer,
	children,
	showCloseButton = true,
	coexistWithPage = false,
	layoutShiftBodyClass,
	isOpen,
	placement = 'right',
	size,
	...rest
}: DrawerProps) => {
	const layout = useLayout();
	const resolvedSize = size ?? (layout === 'mobile' ? 'full' : 'md');
	useEffect(() => {
		if (!layoutShiftBodyClass || !isOpen) return undefined;
		document.body.classList.add(layoutShiftBodyClass);
		return () => document.body.classList.remove(layoutShiftBodyClass);
	}, [isOpen, layoutShiftBodyClass]);

	return (
		<ChakraDrawer isOpen={isOpen} placement={placement} size={resolvedSize} {...rest}>
			{!coexistWithPage && <DrawerOverlay />}
			<DrawerContent
				data-partbay-drawer=""
				bg="white"
				borderLeftWidth={placement === 'left' ? undefined : '1px'}
				borderLeftColor={placement === 'left' ? undefined : 'border.subtle'}
				borderRightWidth={placement === 'left' ? '1px' : undefined}
				borderRightColor={placement === 'left' ? 'border.subtle' : undefined}
				containerProps={coexistWithPage ? SIDE_DRAWER_CONTAINER_PROPS : undefined}
			>
				{showCloseButton ? <DrawerCloseButton /> : null}
				{title ? <DrawerHeader bg="white">{title}</DrawerHeader> : null}
				<DrawerBody>{children}</DrawerBody>
				{footer ? <DrawerFooter gap={3}>{footer}</DrawerFooter> : null}
			</DrawerContent>
		</ChakraDrawer>
	);
};

export const SideDrawer = Drawer;

export default Drawer;
